import React from "react";
import { motion } from "@/motion/appMotion";
import {
  audiences,
  targetAudienceContent,
} from "../../../data/landingPage/targetAudienceContent";
import builtforImage from "../../../assets/image/builtfor.svg";
import {
  LANDING_CONTAINER_PADDED,
  LANDING_INNER,
} from "../../../constants/landingPageLayout";
import useLandingMotion from "../../../hooks/useLandingMotion";

// Custom SVG Icons matching the target audience cards
const HandshakeIcon = () => (
  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 00-3-3.87" />
    <path d="M16 3.13a4 4 0 010 7.75" />
  </svg>
);

const HomeIcon = () => (
  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const SkyscraperIcon = () => (
  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m-1 4h1m-5 10V5a2 2 0 012-2h2a2 2 0 012 2v16m-6 0h6" />
  </svg>
);

const TargetAudience = () => {
  const { getProps } = useLandingMotion();

  // Maps target audience steps with respective custom icons
  const stepperIcons = [
    <HandshakeIcon />,
    <HomeIcon />,
    <SkyscraperIcon />
  ];

  // Specific background gradient circles for icon badges
  const stepperIconBg = [
    "bg-gradient-to-tr from-[#7D3BEE] to-[#6366F1]",
    "bg-gradient-to-tr from-[#3B82F6] to-[#4F46E5]",
    "bg-gradient-to-tr from-[#7D3BEE] to-[#8B5CF6]"
  ];

  return (
    <section
      id="target-audience"
      className="relative w-full bg-[#F1F5F980] py-16 sm:py-20 lg:py-24 clip-x-visible-y"
    >
      <div className={`relative ${LANDING_CONTAINER_PADDED} flex flex-col items-center`}>
        
        {/* Centered Heading and Subtitle Section */}
        <motion.div
          {...getProps({ duration: 0.6 })}
          className="w-full text-center flex flex-col items-center gap-3 mb-12 max-w-[792px] mx-auto"
        >
          <h2
            className="w-full text-[#0F172A] tracking-tight leading-[54px] text-center"
            style={{
              fontFamily: '"Rethink Sans", sans-serif',
              fontWeight: 600,
              fontSize: "clamp(28px, 4.5vw, 48px)",
            }}
          >
            Built for Recruiters & <span className="text-[#724BEF]">Job Consultants</span>
          </h2>
          <p
            className="w-full text-[#64748B] text-center max-w-[680px]"
            style={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 400,
              fontSize: "18px",
              lineHeight: "1.4",
            }}
          >
            {targetAudienceContent.description}
          </p>
        </motion.div>

        {/* Centered Split Screen Container */}
        <div className={`${LANDING_INNER} mx-auto flex max-w-[952px] flex-col items-center justify-center gap-[50px] lg:flex-row lg:gap-[80px]`}>
          
          {/* Left Column: Image Section */}
          <motion.div
            {...getProps({ variant: "fadeLeft", duration: 0.6 })}
            className="w-full lg:w-[469px] shrink-0 flex justify-center items-center"
          >
            <img
              src={builtforImage}
              alt="Built for Recruiters & Job Consultants illustration"
              className="w-full max-w-[469px] h-auto object-contain"
              decoding="async"
            />
          </motion.div>

          {/* Right Column: Stepper Segment Cards */}
          <div className="w-full lg:w-[398px] flex flex-col gap-[32px]">
            {audiences.map((audience, index) => (
              <div
                key={audience.title}
                className="flex flex-row items-start gap-[16px] w-full group cursor-default"
              >
                {/* Stepper Icon Badge */}
                <div
                  className={`w-[50px] h-[50px] rounded-full shrink-0 flex items-center justify-center transition-transform duration-300 group-hover:scale-105 ${stepperIconBg[index]}`}
                >
                  {stepperIcons[index]}
                </div>

                {/* Stepper Text */}
                <div className="flex flex-col gap-[2px] text-left flex-grow">
                  <h3
                    className="font-semibold text-[20px] leading-tight text-[#0F172A]"
                    style={{ fontFamily: '"Rethink Sans", sans-serif' }}
                  >
                    {audience.title}
                  </h3>
                  <p
                    className="text-[14px] leading-relaxed text-[#64748B]"
                    style={{ fontFamily: '"Inter", sans-serif' }}
                  >
                    {audience.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

        </div>

      </div>
    </section>
  );
};

export default TargetAudience;
